import _ from 'lodash';
import { combineReducers } from 'redux';

import SimcInput from 'shared/simcinput';

import types from 'frontend/state/actionTypes';

const selectedReducer = (state = [], action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.INPUT_OPTIMIZE_TEXT: {
      if (payload.azeriteEssences) {
        const rawEssences = _.filter(_.split(payload.azeriteEssences, '/'), e => e.includes(':'));
        return _.map(rawEssences, (essence, key) => {
          const [essenceId, rank] = _.map(_.split(essence, ':'), i => parseInt(i, 10));
          return {
            key,
            slot: key >= 1 ? 1 : key,
            essenceId,
            rank,
          };
        });
      }
      return [];
    }
    case types.TOGGLE_OPTIMIZE_ESSENCE: {
      return _.xorBy(state, [payload], it => `${it.slot}-${it.essenceId}-${it.rank}`);
    }
    case types.TOGGLE_OPTIMIZE_ESSENCE_MINOR_LOCK: {
      const { essenceId, rank } = payload;
      const isSelected = _.find(state, { slot: 1, essenceId });
      if (!isSelected) {
        return [
          ...state,
          {
            slot: 1,
            essenceId,
            rank,
          },
        ];
      }
      return state;
    }
    case types.SET_OPTIMIZE_ESSENCE_RANK: {
      const { slot, essenceId, rank } = payload;
      return [
        // populate with previous state without this essence (if it was there)
        ..._.reject(state, { slot, essenceId }),
        // add new entry with new rank
        {
          slot,
          essenceId,
          rank,
        },
      ];
    }
    case types.LOAD_OPTIMIZE_REPORT: {
      // data on the server is fully "expanded" but the UI is not
      // This maps the server data back to the structure that the UI expects
      // (Majors in slot 0, minors in slot 1)
      const essences = payload.optimize.selectedEssences;
      const transformed = _.uniqBy(
        _.map(essences, essence => ({
          ...essence,
          slot: essence.slot > 1 ? 1 : essence.slot,
        })),
        essence => `${essence.essenceId}-${essence.slot}`,
      );
      return transformed;
    }
    default:
      return state;
  }
};

const passivesReducer = (state = [], action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.INPUT_OPTIMIZE_TEXT: {
      if (payload.azeriteEssences) {
        const passives = _.map(
          _.filter(_.split(payload.azeriteEssences, '/'), e => !e.includes(':')),
          p => parseInt(p, 10),
        );
        return passives;
      }
      return [];
    }
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize.passiveEssences || [];
    default:
      return state;
  }
};

const availableReducer = (state = [], action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.INPUT_OPTIMIZE_TEXT: {
      if (payload.azeriteEssencesAvailable) {
        return _.map(
          _.split(payload.azeriteEssencesAvailable, '/'),
          e => {
            const [essenceId, rank] = _.map(_.split(e, ':'), i => parseInt(i, 10));
            return {
              essenceId,
              rank,
            };
          },
        );
      }
      return [];
    }
    case types.LOAD_OPTIMIZE_REPORT: {
      // new style - available is sent along and stored in data.json
      const available = payload.optimize.azeriteEssencesAvailable;
      if (available) {
        return available;
      }

      // legacy (first week or two of 8.2) - available was not sent along so parse from the raw data
      const essencesAvailable = SimcInput.find(payload.optimize.data, 'azerite_essences_available', true);
      if (essencesAvailable) {
        return _.map(
          _.split(essencesAvailable, '/'),
          e => {
            const [essenceId, rank] = _.map(_.split(e, ':'), i => parseInt(i, 10));
            return {
              essenceId,
              rank,
            };
          },
        );
      }

      return [];
    }

    default:
      return state;
  }
};

// LEGACY - This was used when it only made sense to lock 1 minor essence
const lockedMinorReducer = (state = null, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    // reset on new input
    case types.INPUT_OPTIMIZE_TEXT:
      return null;
    case types.TOGGLE_OPTIMIZE_ESSENCE_MINOR_LOCK: {
      const { essenceId } = payload;
      if (essenceId === state) {
        return null;
      }
      return essenceId;
    }
    // unset on essence toggle
    case types.TOGGLE_OPTIMIZE_ESSENCE: {
      const { essenceId } = payload;
      if (state === essenceId) {
        return null;
      }
      return state;
    }
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize.lockedMinor || null;
    default:
      return state;
  }
};

const lockedMinorsReducer = (state = [], action = {}) => {
  const { type, payload } = action;

  switch (type) {
    // reset on new input
    case types.INPUT_OPTIMIZE_TEXT:
      return [];
    case types.TOGGLE_OPTIMIZE_ESSENCE_MINOR_LOCK: {
      const { essenceId } = payload;
      return _.xor(state, [essenceId]);
    }
    // unset on essence toggle
    case types.TOGGLE_OPTIMIZE_ESSENCE: {
      const { essenceId } = payload;
      if (_.includes(state, essenceId)) {
        return _.without(state, essenceId);
      }
      return state;
    }
    case types.LOAD_OPTIMIZE_REPORT:
      return payload.optimize.lockedMinors || [];
    default:
      return state;
  }
};

export default combineReducers({
  selected: selectedReducer,
  passives: passivesReducer,
  available: availableReducer,
  lockedMinor: lockedMinorReducer,
  lockedMinors: lockedMinorsReducer,
});

const base = state => state.simbot.optimize.azeriteEssences;
const get = path => state => _.get(base(state), path);

export const selectors = {
  selected: get('selected'),
  passives: get('passives'),
  available: get('available'),
  lockedMinor: get('lockedMinor'),
  lockedMinors: get('lockedMinors'),
};
